import React, { useCallback, useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { Sparkles, Loader2, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from 'sonner';
import PublicFooter from '@/components/PublicFooter';

export default function CustomerSignup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    default_address: '',
    postcode: 'HA1',
    accepted_terms: false,
  });

  // All hooks must be declared BEFORE any conditional early return.
  // Previously `useCallback` lived after `if (user) return <Navigate>`,
  // which violated the Rules of Hooks and could trigger React
  // concurrent-rendering crashes after signup.
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      setError(null);
      setSubmitting(true);
      try {
        await api.post('/auth/customer/signup', {
          name: form.name,
          email: form.email.trim().toLowerCase(),
          phone: form.phone,
          password: form.password,
          default_address: form.default_address,
          postcode: form.postcode.toUpperCase(),
          accepted_terms: form.accepted_terms,
          accepted_terms_version: 'v1.0',
        });
        toast.success('Account created');
        window.location.href = '/customer';
      } catch (err) {
        setError(err?.response?.data?.detail || 'Signup failed');
      } finally {
        setSubmitting(false);
      }
    },
    [form],
  );

  if (user && user.role === 'customer') return <Navigate to="/customer" replace />;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6">
      <div className="max-w-md mx-auto">
        <button onClick={() => navigate('/customer/login')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm mb-4" data-testid="customer-signup-back">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </button>
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Create account</h1>
            <p className="text-sm text-slate-500">Book beauty pros at your home</p>
          </div>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Your details</CardTitle>
            <CardDescription>Takes less than 30 seconds.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label>Full name</Label>
                <Input data-testid="customer-signup-name" value={form.name} onChange={set('name')} required className="h-11" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" data-testid="customer-signup-email" value={form.email} onChange={set('email')} required className="h-11" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input type="tel" data-testid="customer-signup-phone" value={form.phone} onChange={set('phone')} required className="h-11" placeholder="+44 7700 900000" />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input type="password" data-testid="customer-signup-password" value={form.password} onChange={set('password')} required minLength={6} className="h-11" />
              </div>
              <div className="h-px bg-slate-100 my-3" />
              <div className="space-y-2">
                <Label>Default address (optional)</Label>
                <Input data-testid="customer-signup-address" value={form.default_address} onChange={set('default_address')} className="h-11" placeholder="Flat 4, 12 High Street" />
              </div>
              <div className="space-y-2">
                <Label>Postcode</Label>
                <Input data-testid="customer-signup-postcode" value={form.postcode} onChange={set('postcode')} required maxLength={8} className="h-11 font-mono" />
              </div>
              <div className="flex items-start gap-2 pt-2">
                <Checkbox
                  id="customer-accept-terms"
                  data-testid="customer-signup-accept-terms"
                  checked={form.accepted_terms}
                  onCheckedChange={(checked) => setForm((f) => ({ ...f, accepted_terms: !!checked }))}
                  className="mt-0.5"
                />
                <Label htmlFor="customer-accept-terms" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                  I agree to the BeautyMateUK{' '}
                  <Link to="/terms" target="_blank" className="text-rose-700 hover:underline">Terms & Conditions</Link>,{' '}
                  <Link to="/privacy" target="_blank" className="text-rose-700 hover:underline">Privacy Policy</Link>, and{' '}
                  <Link to="/safety-policy" target="_blank" className="text-rose-700 hover:underline">Safety Policy</Link>.
                </Label>
              </div>
              {error && <div data-testid="customer-signup-error" className="text-sm rounded-md bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2">{error}</div>}
              <Button type="submit" disabled={submitting || !form.accepted_terms} data-testid="customer-signup-submit" className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-base disabled:opacity-50">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating...</> : 'Create account'}
              </Button>
            </form>
          </CardContent>
        </Card>
        <PublicFooter variant="minimal" />
      </div>
    </div>
  );
}
